/********************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2021, 2024
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} **/

import { useState, useEffect } from 'react';
import { APITypes, getAPIType } from '../utils/APITypes';
import ApiFactory from 'utils/api-factory';
import { Parser } from '@asyncapi/parser';
import { AvroSchemaParser } from '@asyncapi/avro-schema-parser';
import { OpenAPISchemaParser } from '@asyncapi/openapi-schema-parser';
import { cloneDeep } from 'lodash';
const parser = new Parser({});
parser.registerSchemaParser(AvroSchemaParser());
parser.registerSchemaParser(OpenAPISchemaParser());

export async function _parse(api) {
  const validateOptions = { allowedSeverity: { error: true, warning: true } }
  const { document, diagnostics } = await parser.parse(api, { validateOptions })
  if (!document) {
    console.error('Failed to parse api')
    console.error(diagnostics)
  }
  return document;
}

export async function _validate(api) {
  return parser.validate(api) 
}

const errorDetails = (error) => {
  const { title = '', validationErrors = [], detail = '' } = error;
  const validationMessages = validationErrors.map(({ title }) => title);
  const errorDetails = [title, detail, ...validationMessages];
  return errorDetails;
};

const useCreateApiHandler = (api) => {
  const [{ originalApi, apiHandler, loading, error }, updateState] = useState({ originalApi: api, apiHandler: null, loading: true, error: null });

  useEffect(() => {
    updateState((prev) => ({ ...prev, loading: true }));
    let cancel = false;
    if (getAPIType(api) === APITypes.asyncapi) {
      (async () => {
        try {
          const clonedApi = cloneDeep(api);
          const res = await _parse(api);

          if (cancel) return;
          updateState((prev) => ({ ...prev, originalApi: clonedApi, apiHandler: ApiFactory(res), error: null, loading: false }));
        } catch (error) {
          if (cancel) return;
          console.error('Errors detected while parsing:', error);
          const details = errorDetails(error).join('\n');
          updateState((prev) => ({ ...prev, error: details, loading: false }));
        }
      })();
    } else {
      updateState((prev) => ({ ...prev, originalApi: api, apiHandler: ApiFactory(api), error: null, loading: false }));
    }
    return () => {
      cancel = true;
    };
  }, [api]);

  return { originalApi, apiHandler, loading, error };
};

export { useCreateApiHandler };
